import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  FiUsers, 
  FiCalendar, 
  FiBarChart2, 
  FiSettings, 
  FiTrendingUp, 
  FiClock, 
  FiDatabase,
  FiFileText,
  FiDownload,
  FiRefreshCw,
  FiSearch
} from 'react-icons/fi';
import { getCookie } from '../utils/cookies';
import toast from 'react-hot-toast';

const StatCard = ({ icon: Icon, title, value, subtitle, trend, color }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-700/50"
  >
    <div className="flex items-center justify-between">
      <div className="flex-1">
        <div className="flex items-center space-x-3 mb-2">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
          <h3 className="text-gray-300 font-medium">{title}</h3>
        </div>
        <div className="space-y-1">
          <p className="text-2xl font-bold text-white">{value}</p>
          {subtitle && <p className="text-sm text-gray-400">{subtitle}</p>}
          {trend && (
            <div className="flex items-center space-x-1">
              <FiTrendingUp className="w-4 h-4 text-green-400" />
              <span className="text-sm text-green-400">{trend}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  </motion.div>
);

const RecentActivity = ({ activities }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 shadow-lg border border-gray-700/50"
  >
    <div className="flex items-center justify-between mb-6">
      <h3 className="text-xl font-bold text-white">Recent Activity</h3>
      <button className="text-blue-400 hover:text-blue-300 transition-colors">
        <FiRefreshCw className="w-5 h-5" />
      </button>
    </div>
    <div className="space-y-4 max-h-96 overflow-y-auto">
      {activities.map((activity, index) => (
        <div key={index} className="flex items-start space-x-3 p-3 bg-gray-700/30 rounded-lg">
          <div className="w-2 h-2 bg-blue-400 rounded-full mt-2"></div>
          <div className="flex-1">
            <p className="text-white font-medium">{activity.action}</p>
            <p className="text-gray-400 text-sm">{activity.user}</p>
            <p className="text-gray-500 text-xs">{activity.timestamp}</p>
          </div>
        </div>
      ))}
    </div>
  </motion.div>
);

const QuickActions = ({ onAction }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 shadow-lg border border-gray-700/50"
  >
    <h3 className="text-xl font-bold text-white mb-6">Quick Actions</h3>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <button
        onClick={() => onAction('export-data')}
        className="flex items-center space-x-3 p-4 bg-blue-600/20 hover:bg-blue-600/30 border border-blue-500/30 rounded-lg transition-all duration-200 group"
      >
        <FiDownload className="w-5 h-5 text-blue-400 group-hover:scale-110 transition-transform" />
        <span className="text-white font-medium">Export Data</span>
      </button>
      <button
        onClick={() => onAction('manage-users')}
        className="flex items-center space-x-3 p-4 bg-purple-600/20 hover:bg-purple-600/30 border border-purple-500/30 rounded-lg transition-all duration-200 group"
      >
        <FiUsers className="w-5 h-5 text-purple-400 group-hover:scale-110 transition-transform" />
        <span className="text-white font-medium">Manage Users</span>
      </button>
      <button
        onClick={() => onAction('system-settings')}
        className="flex items-center space-x-3 p-4 bg-green-600/20 hover:bg-green-600/30 border border-green-500/30 rounded-lg transition-all duration-200 group"
      >
        <FiSettings className="w-5 h-5 text-green-400 group-hover:scale-110 transition-transform" />
        <span className="text-white font-medium">System Settings</span>
      </button>
      <button
        onClick={() => onAction('view-reports')}
        className="flex items-center space-x-3 p-4 bg-orange-600/20 hover:bg-orange-600/30 border border-orange-500/30 rounded-lg transition-all duration-200 group"
      >
        <FiFileText className="w-5 h-5 text-orange-400 group-hover:scale-110 transition-transform" />
        <span className="text-white font-medium">View Reports</span>
      </button>
    </div>
  </motion.div>
);

const UserManagement = ({ users, onUserAction }) => {
  const [searchTerm, setSearchTerm] = useState('');
  
  const filteredUsers = users.filter(user => 
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 shadow-lg border border-gray-700/50"
    >
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-white">User Management</h3>
        <div className="relative">
          <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-gray-700">
              <th className="pb-3 text-gray-300 font-medium">User</th>
              <th className="pb-3 text-gray-300 font-medium">Status</th>
              <th className="pb-3 text-gray-300 font-medium">Last Active</th>
              <th className="pb-3 text-gray-300 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="space-y-2">
            {filteredUsers.slice(0, 10).map((user, index) => (
              <tr key={index} className="border-b border-gray-700/50">
                <td className="py-3">
                  <div>
                    <p className="text-white font-medium">{user.name}</p>
                    <p className="text-gray-400 text-sm">{user.email}</p>
                  </div>
                </td>
                <td className="py-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    user.status === 'active' 
                      ? 'bg-green-600/20 text-green-400 border border-green-500/30'
                      : 'bg-gray-600/20 text-gray-400 border border-gray-500/30'
                  }`}>
                    {user.status}
                  </span>
                </td>
                <td className="py-3 text-gray-400 text-sm">{user.lastActive}</td>
                <td className="py-3">
                  <button
                    onClick={() => onUserAction(user.id, 'view')}
                    className="text-blue-400 hover:text-blue-300 transition-colors mr-3"
                  >
                    View
                  </button>
                  <button
                    onClick={() => onUserAction(user.id, 'edit')}
                    className="text-purple-400 hover:text-purple-300 transition-colors"
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </motion.div>
  );
};

// Heat map component for class timings
const ClassTimingHeatMap = () => {
  const [classData, setClassData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [heatMapData, setHeatMapData] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [filteredMode, setFilteredMode] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [totalStudentCount, setTotalStudentCount] = useState(0);

  // Time slots (8 AM to 10 PM in 30-minute intervals)
  const timeSlots = [];
  for (let hour = 8; hour < 22; hour++) {
    timeSlots.push(`${hour > 12 ? hour - 12 : hour}:00 ${hour >= 12 ? 'PM' : 'AM'}`);
    timeSlots.push(`${hour > 12 ? hour - 12 : hour}:30 ${hour >= 12 ? 'PM' : 'AM'}`);
  }

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

  // Debounced autocomplete
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (searchTerm.trim() === '') {
        setSuggestions([]);
        return;
      }
      try {
        const params = new URLSearchParams({
          title: encodeURIComponent(searchTerm)
        });
        // Admin can search all campuses, so we don't add campus param
        const response = await axios.get(
          `https://query.ruturbo.com/autocomplete?${params.toString()}`
        );
        setSuggestions(response.data);
      } catch (error) {
        console.error('Error fetching suggestions:', error);
        setSuggestions([]);
      }
    };

    const timeoutId = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const handleSearch = async (courseTitle) => {
    if (!courseTitle.trim()) return;
    
    setSearchLoading(true);
    try {
      // Search for course with campus header
      const params = new URLSearchParams({
        title: courseTitle,
        page: 1
      });
      const searchResponse = await axios.get(
        `https://query.ruturbo.com/search?${params.toString()}`,
        {
          headers: {
            campus: null // Admin can search all campuses
          }
        }
      );
      
      if (searchResponse.data?.results?.length > 0) {
        const course = searchResponse.data.results[0];
        
        // Parse sections data (it's a string that needs to be parsed as JSON)
        let sections;
        try {
          // The sections field is a string representation of a Python list, need to parse it
          const sectionsStr = course.sections.replace(/'/g, '"').replace(/True/g, 'true').replace(/False/g, 'false');
          sections = JSON.parse(sectionsStr);
        } catch (parseError) {
          console.error('Error parsing sections:', parseError);
          toast('Error parsing course sections');
          return;
        }
        
        const sectionIndexes = sections.map(section => section.index).filter(Boolean);
        
        if (sectionIndexes.length > 0) {
          // Configuration for heat map API (this one needs authentication)
          const config = {
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'Authorization': getCookie('session_id')
            }
          };

          // Get heat map data for these sections
          const heatMapResponse = await axios.post('https://api.ruturbo.com/get-heat-map', {
            class_indexes: sectionIndexes
          }, config);
          
          if (heatMapResponse.data) {
            // Process heat map data for student availability
            const processedHeatMap = await processStudentAvailabilityData(heatMapResponse.data);
            setHeatMapData(processedHeatMap);
            setFilteredMode(true);
            setSelectedCourse(course);
            toast('Showing availability for ' + courseTitle);
          }
        } else {
          toast('No sections found for this course');
        }
      } else {
        toast('Course not found');
      }
    } catch (error) {
      console.error('Search error:', error);
      if (error.response?.status === 401) {
        toast('Authentication failed. Please log in again.');
      } else {
        toast('Failed to search course');
      }
    } finally {
      setSearchLoading(false);
      setSuggestions([]);
      setSearchTerm('');
    }
  };

  const processStudentAvailabilityData = async (heatMapApiResponse) => {
    try {
      console.log('Heat map API response:', heatMapApiResponse);
      
      // New API format returns { counts: {index: freq, ...}, student_count: N }
      let indexFrequencyMap = heatMapApiResponse;
      let totalStudentCount = 0;

      if (heatMapApiResponse && heatMapApiResponse.counts) {
        indexFrequencyMap = heatMapApiResponse.counts;
        totalStudentCount = heatMapApiResponse.student_count || 0;
      }

      // Set the total student count for legend calculation
      setTotalStudentCount(totalStudentCount);

      // Extract indexes and their frequencies from the response
      const indexes = Object.keys(indexFrequencyMap);
      
      if (indexes.length === 0) {
        console.log('No indexes found in heat map response');
        return initializeEmptyHeatMap();
      }
      
      console.log('Indexes to fetch course data for:', indexes);
      console.log('Total student count:', totalStudentCount);
      
      // Configuration for course data API
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': getCookie('session_id'),
          campus: null // Admin can see all campuses
        }
      };
      
      // Fetch course data for all indexes
      const courseResponse = await axios.post('https://query.ruturbo.com/get_course_data', {
        indexes: indexes
      }, config);
      
      console.log('Course data response:', courseResponse.data);
      
      // Process the course data and create heat map
      const heatMap = initializeEmptyHeatMap();
      
      console.log('Index frequency map:', indexFrequencyMap);
      console.log('Available keys in frequency map:', Object.keys(indexFrequencyMap));
      
      courseResponse.data.forEach(course => {
        console.log('Full course object:', course);
        
        // Try different possible field names for section index
        const possibleIndexFields = [
          course.sectionIndex,
          course.index,
          course.section_index,
          course.courseIndex,
          course.id,
          course.section?.index
        ].filter(Boolean);
        
        console.log('Possible index fields for course:', possibleIndexFields);
        
        // Find the matching frequency
        let frequency = 0;
        let matchedIndex = null;
        
        for (const indexField of possibleIndexFields) {
          if (indexFrequencyMap[indexField]) {
            frequency = indexFrequencyMap[indexField];
            matchedIndex = indexField;
            break;
          }
        }
        
        // If no direct match, try string conversion
        if (frequency === 0) {
          for (const indexField of possibleIndexFields) {
            const stringIndex = String(indexField);
            if (indexFrequencyMap[stringIndex]) {
              frequency = indexFrequencyMap[stringIndex];
              matchedIndex = stringIndex;
              break;
            }
          }
        }
        
        console.log(`Course processing: index fields ${possibleIndexFields}, matched ${matchedIndex}, frequency ${frequency}`);
        
        // Use frequency of 1 as fallback if no match found
        if (frequency === 0) {
          frequency = 1;
          console.log('No frequency match found, using fallback frequency of 1');
        }
        
        // Ensure meetingTimes is an array
        const meetingTimes = Array.isArray(course.meetingTimes) ? course.meetingTimes : [];
        
        meetingTimes.forEach(meeting => {
          console.log('Raw meeting data:', meeting);
          
          // Check for different possible field names
          const meetingDays = meeting.Meetingdays || meeting.days;
          const startTime = meeting.StartTime || meeting.startTime;
          const endTime = meeting.EndTime || meeting.endTime;
          
          if (!meetingDays || !startTime || !endTime || 
              meetingDays === 'N/A' || startTime === 'N/A' || endTime === 'N/A') {
            console.log('Skipping meeting due to missing data:', { meetingDays, startTime, endTime });
            return;
          }
          
          // Convert times
          const startTime12 = convertTime24to12(startTime);
          const endTime12 = convertTime24to12(endTime);
          
          console.log(`Time conversion: ${startTime} -> ${startTime12}, ${endTime} -> ${endTime12}`);
          
          // Handle all days for multi-day classes (e.g., "MWF" -> Monday, Wednesday, Friday)
          const dayNames = convertAllDayCodes(meetingDays);
          
          dayNames.forEach(dayName => {
            if (!dayName || !heatMap[dayName]) {
              console.log(`Skipping invalid day: ${dayName}`);
              return;
            }
            
            console.log(`Processing meeting: ${dayName} ${startTime12} - ${endTime12} with frequency ${frequency}`);
            
            // Add to heat map for each time slot this class occupies
            timeSlots.forEach(slot => {
              if (isTimeInRange(slot, startTime12, endTime12)) {
                const oldValue = heatMap[dayName][slot];
                heatMap[dayName][slot] += frequency;
                console.log(`Added ${frequency} to ${dayName} ${slot}: ${oldValue} -> ${heatMap[dayName][slot]}`);
              }
            });
          });
        });
      });
      
      console.log('Final processed heat map:', heatMap);
      return heatMap;
      
    } catch (error) {
      console.error('Error processing student availability data:', error);
      toast('Failed to process student availability data');
      return initializeEmptyHeatMap();
    }
  };

  const convertAllDayCodes = (dayCode) => {
    const dayMap = {
      'M': 'Monday',
      'T': 'Tuesday', 
      'W': 'Wednesday',
      'H': 'Thursday',
      'F': 'Friday',
      'S': 'Saturday',
      'U': 'Sunday'
    };
    
    if (!dayCode) return [];
    
    // Handle single day
    if (dayCode.length === 1 && dayMap[dayCode]) {
      return [dayMap[dayCode]];
    }
    
    // Handle multiple days (like "MWF", "TH", etc.)
    const days = [];
    for (let i = 0; i < dayCode.length; i++) {
      const currentChar = dayCode[i];
      
      // Handle special case for Thursday (H)
      if (currentChar === 'T' && i + 1 < dayCode.length && dayCode[i + 1] === 'H') {
        days.push('Thursday');
        i++; // Skip the 'H' since we processed 'TH'
      } else if (currentChar === 'H' && (i === 0 || dayCode[i - 1] !== 'T')) {
        // Handle standalone 'H' for Thursday
        days.push('Thursday');
      } else if (currentChar !== 'H' && dayMap[currentChar]) {
        days.push(dayMap[currentChar]);
      }
    }
    
    console.log(`Converted day code "${dayCode}" to days:`, days);
    return days;
  };

  const initializeEmptyHeatMap = () => {
    const heatMap = {};
    days.forEach(day => {
      heatMap[day] = {};
      timeSlots.forEach(slot => {
        heatMap[day][slot] = 0;
      });
    });
    return heatMap;
  };

  const convertTime24to12 = (timeInput) => {
    if (!timeInput) return '';
    
    const timeStr = String(timeInput).trim();
    
    // If it's already in 12-hour format (contains AM/PM), return as-is
    if (timeStr.includes('AM') || timeStr.includes('PM')) {
      return timeStr;
    }
    
    // Handle 24-hour format (4 digits like "1550")
    if (timeStr.length === 4 && /^\d{4}$/.test(timeStr)) {
      const hours = parseInt(timeStr.substring(0, 2));
      const minutes = timeStr.substring(2, 4);
      
      if (hours === 0) return `12:${minutes} AM`;
      if (hours < 12) return `${hours}:${minutes} AM`;
      if (hours === 12) return `12:${minutes} PM`;
      return `${hours - 12}:${minutes} PM`;
    }
    
    // Handle other time formats like "15:50" or "3:50"
    if (timeStr.includes(':')) {
      const [hourStr, minuteStr] = timeStr.split(':');
      const hours = parseInt(hourStr);
      const minutes = minuteStr;
      
      if (isNaN(hours)) return '';
      
      if (hours === 0) return `12:${minutes} AM`;
      if (hours < 12) return `${hours}:${minutes} AM`;
      if (hours === 12) return `12:${minutes} PM`;
      return `${hours - 12}:${minutes} PM`;
    }
    
    console.log(`Unable to parse time format: "${timeStr}"`);
    return '';
  };

  const isTimeInRange = (slot, startTime, endTime) => {
    const slotTime = parseTime(slot);
    const start = parseTime(startTime);
    const end = parseTime(endTime);
    
    if (slotTime === null || start === null || end === null) return false;
    
    return slotTime >= start && slotTime < end;
  };

  const resetToAllClasses = () => {
    setFilteredMode(false);
    setSelectedCourse(null);
    setTotalStudentCount(0);
    generateHeatMapData();
    toast('Showing all class density');
  };

  const fetchClassData = async () => {
    try {
      setLoading(true);
      
      // Configuration for axios requests
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': getCookie('session_id')
        }
      };
      
      // First API call to get all current class indexes
      const indexResponse = await axios.get('https://api.ruturbo.com/get-all-currentclasses', config);
      console.log('Index Response:', indexResponse.data);
      
      // Flatten the array and remove nulls and empty arrays (matching Statistics.js pattern)
      const flattenedIndexes = indexResponse.data.currentClasses
        .filter(group => Array.isArray(group) && group.length > 0)
        .flat()
        .sort((a, b) => a - b);
      
      console.log('Flattened indexes:', flattenedIndexes);
      
      if (flattenedIndexes.length === 0) {
        console.log('No indexes found');
        setClassData([]);
        setHeatMapData({});
        return;
      }
      
      // Second API call to get course data for all indexes at once
      const body = JSON.stringify({ indexes: flattenedIndexes });
      const courseResponse = await axios.post('https://query.ruturbo.com/get_course_data', body, {
        ...config,
        headers: {
          ...config.headers,
          campus: null // Admin can see all campuses
        }
      });
      
      console.log('Raw Course Response:', courseResponse.data);
      
      // Process the course data (matching Statistics.js pattern)
      const processedData = courseResponse.data.map(course => {
        console.log('Processing course data:', course);
        console.log('Original meeting times:', course.meetingTimes);
        
        const processed = {
          ...course,
          meetingTimes: Array.isArray(course.meetingTimes) ? course.meetingTimes : []
        };
        
        console.log('Processed meeting times:', processed.meetingTimes);
        return processed;
      });
      
      console.log('Final processed course data:', processedData);
      setClassData(processedData);
      generateHeatMapData(processedData);
      
    } catch (error) {
      console.error('Error fetching class data:', error);
      toast('Failed to load class timing data');
    } finally {
      setLoading(false);
    }
  };

  const parseTime = (timeStr) => {
    if (!timeStr || timeStr === '') return null;
    
    const match = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (!match) return null;
    
    let hours = parseInt(match[1]);
    const minutes = parseInt(match[2]);
    const period = match[3].toUpperCase();
    
    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
    
    return hours + minutes / 60;
  };

  const generateHeatMapData = (courses = classData) => {
    const heatMap = {};
    
    // Initialize heat map
    days.forEach(day => {
      heatMap[day] = {};
      timeSlots.forEach(slot => {
        heatMap[day][slot] = 0;
      });
    });
    
    // Ensure courses is an array
    if (!Array.isArray(courses)) {
      setHeatMapData(heatMap);
      return;
    }
    
    // Count classes for each time slot
    courses.forEach(course => {
      course.meetingTimes?.forEach(meeting => {
        if (!meeting.days || !meeting.startTime || !meeting.endTime) return;
        
        const startTime = parseTime(meeting.startTime);
        const endTime = parseTime(meeting.endTime);
        
        if (startTime === null || endTime === null) return;
        
        // For each 30-minute slot that overlaps with the class
        timeSlots.forEach(slot => {
          const slotTime = parseTime(slot);
          if (slotTime !== null && slotTime >= startTime && slotTime < endTime) {
            if (heatMap[meeting.days] && heatMap[meeting.days][slot] !== undefined) {
              heatMap[meeting.days][slot]++;
            }
          }
        });
      });
    });
    
    setHeatMapData(heatMap);
  };

  // Dynamic color scaling based on count / maxCount
  const getHeatMapStyle = (count, max) => {
    if (filteredMode) {
      // Student availability mode: Use discrete ranges matching the legend
      const percentage = totalStudentCount === 0 ? 0 : (count / totalStudentCount) * 100;
      
      if (count === 0) {
        return { backgroundColor: '#1F2937', border: '1px solid #4B5563' }; // gray-800 instead of transparent
      } else if (percentage <= 20) {
        return { backgroundColor: '#4ADE80', border: '1px solid #22C55E' }; // green-400
      } else if (percentage <= 40) {
        return { backgroundColor: '#FACC15', border: '1px solid #EAB308' }; // yellow-400
      } else if (percentage <= 70) {
        return { backgroundColor: '#F97316', border: '1px solid #EA580C' }; // orange-500
      } else if (percentage <= 90) {
        return { backgroundColor: '#EF4444', border: '1px solid #DC2626' }; // red-500
      } else {
        return { backgroundColor: '#DC2626', border: '1px solid #B91C1C' }; // red-600
      }
    } else {
      // Class density mode: Use discrete ranges
      if (count === 0) {
        return { backgroundColor: '#1F2937', border: '1px solid #4B5563' }; // gray-800 (light grey)
      } else if (count <= 2) {
        return { backgroundColor: '#3B82F6', border: '1px solid #2563EB' }; // blue-500
      } else if (count <= 5) {
        return { backgroundColor: '#2563EB', border: '1px solid #1D4ED8' }; // blue-600
      } else if (count <= 10) {
        return { backgroundColor: '#8B5CF6', border: '1px solid #7C3AED' }; // purple-500
      } else if (count <= 15) {
        return { backgroundColor: '#F97316', border: '1px solid #EA580C' }; // orange-500
      } else {
        return { backgroundColor: '#EF4444', border: '1px solid #DC2626' }; // red-500
      }
    }
  };

  const getMaxCount = () => {
    let max = 0;
    Object.values(heatMapData).forEach(dayData => {
      Object.values(dayData).forEach(count => {
        max = Math.max(max, count);
      });
    });
    return max;
  };

  useEffect(() => {
    fetchClassData();
  }, []);

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 shadow-lg border border-gray-700/50"
      >
        <div className="flex items-center justify-center py-12">
          <FiRefreshCw className="w-8 h-8 text-blue-400 animate-spin mr-3" />
          <span className="text-gray-400">Loading class timing data...</span>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 shadow-lg border border-gray-700/50"
    >
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-bold text-white">
            {filteredMode ? 'Student Availability Heat Map' : 'Class Timing Heat Map'}
          </h3>
          <p className="text-gray-400 text-sm">
            {filteredMode 
              ? `Heat Map of students sniping ${selectedCourse?.title || 'selected course'}`
              : 'Visual representation of class density throughout the week'
            }
          </p>
        </div>
        <button
          onClick={filteredMode ? resetToAllClasses : fetchClassData}
          className="text-blue-400 hover:text-blue-300 transition-colors"
        >
          <FiRefreshCw className="w-5 h-5" />
        </button>
      </div>

      {/* Search Bar */}
      <div className="space-y-6">
        {/* Search Bar */}
        <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-xl p-6 border border-gray-700/50">
          <h3 className="text-lg font-semibold text-white mb-4">
            Search Course for Student Availability
          </h3>
          <div className="relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && searchTerm.trim()) {
                  handleSearch(searchTerm);
                }
              }}
              placeholder="Search for a course (e.g., Data Structures, CS112)"
              className="w-full bg-gray-700 text-white placeholder-gray-400 rounded-lg px-4 py-3 border border-gray-600 focus:border-blue-500 focus:outline-none transition-colors"
            />
            
            {/* Autocomplete suggestions */}
            {suggestions.length > 0 && (
              <div className="absolute z-50 w-full mt-2 bg-gray-800 border border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {suggestions.map((suggestion, index) => (
                  <div
                    key={index}
                    onClick={() => handleSearch(suggestion)}
                    className="px-4 py-3 hover:bg-gray-700 cursor-pointer text-white border-b border-gray-700 last:border-b-0"
                  >
                    {suggestion}
                  </div>
                ))}
              </div>
            )}
            
            {searchLoading && (
              <div className="absolute right-3 top-3">
                <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full"></div>
              </div>
            )}
          </div>
          
          {filteredMode && (
            <div className="mt-4 flex items-center justify-between">
              <div className="text-sm text-green-400">
                ✓ Showing student availability heat map
              </div>
              <button
                onClick={resetToAllClasses}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition-colors"
              >
                Show All Classes
              </button>
            </div>
          )}
        </div>

        {/* Heat Map */}
        <motion.div 
          className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-xl p-6 border border-gray-700/50"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Legend */}
          <motion.div 
            className="mb-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.2 }}
          >
            <h4 className="text-sm font-medium text-gray-300 mb-3">Color Legend:</h4>
            <div className="flex flex-wrap items-center gap-4 text-xs">
              {filteredMode ? (
                // Dynamic student availability legend based on actual student count
                <>
                  <motion.div 
                    className="flex items-center gap-2"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.15, duration: 0.2 }}
                  >
                    <div className="w-4 h-4 bg-gray-800 border border-gray-600 rounded"></div>
                    <span className="text-gray-400 font-medium">0 Students (Available)</span>
                  </motion.div>
                  <motion.div 
                    className="flex items-center gap-2"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.18, duration: 0.2 }}
                  >
                    <div className="w-4 h-4 bg-green-400 border border-green-300 rounded"></div>
                    <span className="text-green-300">1-{Math.ceil(totalStudentCount * 0.2)} Students</span>
                  </motion.div>
                  <motion.div 
                    className="flex items-center gap-2"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.21, duration: 0.2 }}
                  >
                    <div className="w-4 h-4 bg-yellow-400 border border-yellow-300 rounded"></div>
                    <span className="text-yellow-300">{Math.ceil(totalStudentCount * 0.2) + 1}-{Math.ceil(totalStudentCount * 0.4)} Students</span>
                  </motion.div>
                  <motion.div 
                    className="flex items-center gap-2"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.24, duration: 0.2 }}
                  >
                    <div className="w-4 h-4 bg-orange-500 border border-orange-400 rounded"></div>
                    <span className="text-orange-400">{Math.ceil(totalStudentCount * 0.4) + 1}-{Math.ceil(totalStudentCount * 0.7)} Students</span>
                  </motion.div>
                  <motion.div 
                    className="flex items-center gap-2"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.27, duration: 0.2 }}
                  >
                    <div className="w-4 h-4 bg-red-500 border border-red-400 rounded"></div>
                    <span className="text-red-400">{Math.ceil(totalStudentCount * 0.7) + 1}-{Math.ceil(totalStudentCount * 0.9)} Students</span>
                  </motion.div>
                  <motion.div 
                    className="flex items-center gap-2"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3, duration: 0.2 }}
                  >
                    <div className="w-4 h-4 bg-red-600 border border-red-500 rounded"></div>
                    <span className="text-red-500 font-medium">{Math.ceil(totalStudentCount * 0.9) + 1}-{totalStudentCount} Students (Very Busy)</span>
                  </motion.div>
                  <motion.div 
                    className="text-gray-300 text-xs ml-4"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.33, duration: 0.2 }}
                  >
                    Total Students: <span className="font-semibold text-white">{totalStudentCount}</span>
                  </motion.div>
                </>
              ) : (
                // Class density legend
                <>
                  <motion.div 
                    className="flex items-center gap-2"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.15, duration: 0.2 }}
                  >
                    <div className="w-4 h-4 bg-gray-800 border border-gray-600 rounded"></div>
                    <span className="text-gray-400">No Classes</span>
                  </motion.div>
                  <motion.div 
                    className="flex items-center gap-2"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.18, duration: 0.2 }}
                  >
                    <div className="w-4 h-4 bg-blue-500 border border-blue-400 rounded"></div>
                    <span className="text-blue-400">Light (1-2)</span>
                  </motion.div>
                  <motion.div 
                    className="flex items-center gap-2"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.21, duration: 0.2 }}
                  >
                    <div className="w-4 h-4 bg-blue-600 border border-blue-500 rounded"></div>
                    <span className="text-blue-500">Moderate (3-5)</span>
                  </motion.div>
                  <motion.div 
                    className="flex items-center gap-2"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.24, duration: 0.2 }}
                  >
                    <div className="w-4 h-4 bg-purple-500 border border-purple-400 rounded"></div>
                    <span className="text-purple-400">Getting Busy (6-10)</span>
                  </motion.div>
                  <motion.div 
                    className="flex items-center gap-2"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.27, duration: 0.2 }}
                  >
                    <div className="w-4 h-4 bg-orange-500 border border-orange-400 rounded"></div>
                    <span className="text-orange-400">Heavy (11-15)</span>
                  </motion.div>
                  <motion.div 
                    className="flex items-center gap-2"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3, duration: 0.2 }}
                  >
                    <div className="w-4 h-4 bg-red-500 border border-red-400 rounded"></div>
                    <span className="text-red-400">Very Heavy (16+)</span>
                  </motion.div>
                </>
              )}
            </div>
          </motion.div>

          <motion.div 
            className="overflow-x-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.3 }}
          >
            <div className="min-w-max">
              {/* Day headers */}
              <motion.div 
                className="grid grid-cols-6 gap-1 mb-2"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25, duration: 0.2 }}
              >
                <div className="w-16"></div> {/* Empty cell for time labels */}
                {days.map((day, index) => (
                  <motion.div 
                    key={day} 
                    className="text-center text-sm font-medium text-gray-300 py-2"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + index * 0.03, duration: 0.2 }}
                  >
                    {day.slice(0, 3)}
                  </motion.div>
                ))}
              </motion.div>
              
              {/* Time slots and heat map cells */}
              {timeSlots.map((timeSlot, timeIndex) => (
                <motion.div 
                  key={timeSlot} 
                  className="grid grid-cols-6 gap-1 mb-1"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 + timeIndex * 0.005, duration: 0.2 }}
                >
                  <motion.div 
                    className="w-16 text-xs text-gray-400 py-1 text-right pr-2"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.45 + timeIndex * 0.005, duration: 0.15 }}
                  >
                    {timeIndex % 2 === 0 ? timeSlot : ''}
                  </motion.div>
                  {days.map((day, dayIndex) => {
                    const value = heatMapData[day]?.[timeSlot] || 0;
                    return (
                      <motion.div
                        key={`${day}-${timeSlot}`}
                        className={`h-5 rounded hover:ring-2 hover:ring-white hover:scale-110 cursor-pointer shadow-sm relative`}
                        style={getHeatMapStyle(value, getMaxCount())}
                        title={`${day} ${timeSlot}: ${value} ${filteredMode ? `student${value !== 1 ? 's' : ''} busy` : `class${value !== 1 ? 'es' : ''}`}${filteredMode ? ` (Frequency: ${value})` : ''}`}
                        initial={{ 
                          opacity: 0, 
                          scale: 0,
                          backgroundColor: '#374151'
                        }}
                        animate={{ 
                          opacity: 1, 
                          scale: 1,
                          ...getHeatMapStyle(value, getMaxCount())
                        }}
                        transition={{ 
                          delay: 0.5 + timeIndex * 0.005 + dayIndex * 0.01, 
                          duration: 0.25,
                          type: "spring",
                          stiffness: 150,
                          damping: 20
                        }}
                        whileHover={{
                          scale: 1.15,
                          zIndex: 50,
                          transition: { duration: 0.15 }
                        }}
                        whileTap={{
                          scale: 0.95,
                          transition: { duration: 0.1 }
                        }}
                      />
                    );
                  })}
                </motion.div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>
      
      {/* Summary Stats */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
        <div className="bg-gray-700/50 rounded-lg p-3">
          <div className="text-lg font-bold text-white">{classData.length}</div>
          <div className="text-xs text-gray-400">{filteredMode ? 'Students Tracked' : 'Total Classes'}</div>
        </div>
        <div className="bg-gray-700/50 rounded-lg p-3">
          <div className="text-lg font-bold text-white">
            {filteredMode ? (selectedCourse ? 1 : 0) : new Set(classData.map(c => c.courseTitle)).size}
          </div>
          <div className="text-xs text-gray-400">{filteredMode ? 'Course Selected' : 'Unique Courses'}</div>
        </div>
        <div className="bg-gray-700/50 rounded-lg p-3">
          <div className="text-lg font-bold text-white">
            {filteredMode ? new Set(classData.map(c => c.sectionIndex)).size : new Set(classData.map(c => c.professor)).size}
          </div>
          <div className="text-xs text-gray-400">{filteredMode ? 'Sections Available' : 'Professors'}</div>
        </div>
        <div className="bg-gray-700/50 rounded-lg p-3">
          <div className="text-lg font-bold text-white">{getMaxCount()}</div>
          <div className="text-xs text-gray-400">{filteredMode ? 'Peak Students' : 'Peak Classes'}</div>
        </div>
      </div>
    </motion.div>
  );
};

const AdminDashboard = ({ userName, email, isAdmin, membership }) => {
  const [dashboardData, setDashboardData] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalSnipes: 0,
    successfulSnipes: 0,
    systemHealth: 'Good',
    recentActivity: [],
    users: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      // Mock data for now - replace with actual API calls
      const mockData = {
        totalUsers: 1247,
        activeUsers: 892,
        totalSnipes: 15430,
        successfulSnipes: 12850,
        systemHealth: 'Good',
        recentActivity: [
          {
            action: 'New user registration',
            user: 'john.doe@rutgers.edu',
            timestamp: '2 minutes ago'
          },
          {
            action: 'Schedule upload processed',
            user: 'jane.smith@rutgers.edu',
            timestamp: '5 minutes ago'
          },
          {
            action: 'Successful snipe completed',
            user: 'mike.johnson@rutgers.edu',
            timestamp: '8 minutes ago'
          },
          {
            action: 'System maintenance completed',
            user: 'System',
            timestamp: '15 minutes ago'
          }
        ],
        users: [
          {
            id: 1,
            name: 'John Doe',
            email: 'john.doe@rutgers.edu',
            status: 'active',
            lastActive: '2 hours ago'
          },
          {
            id: 2,
            name: 'Jane Smith',
            email: 'jane.smith@rutgers.edu',
            status: 'active',
            lastActive: '1 hour ago'
          },
          {
            id: 3,
            name: 'Mike Johnson',
            email: 'mike.johnson@rutgers.edu',
            status: 'inactive',
            lastActive: '2 days ago'
          }
        ]
      };
      setDashboardData(mockData);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAction = (action) => {
    switch (action) {
      case 'export-data':
        toast.success('Data export initiated');
        break;
      case 'manage-users':
        toast('User management panel opened');
        break;
      case 'system-settings':
        toast('System settings opened');
        break;
      case 'view-reports':
        toast('Reports view opened');
        break;
      default:
        break;
    }
  };

  const handleUserAction = (userId, action) => {
    toast(`${action} action for user ${userId}`);
  };

  if (membership !== "Admin") {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Access Denied</h2>
          <p className="text-gray-400">You don't have permission to access the admin dashboard.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <FiRefreshCw className="w-8 h-8 text-blue-400 animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 text-transparent bg-clip-text">
            Admin Dashboard
          </h1>
          <p className="mt-2 text-gray-400">Welcome back, {userName}</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            icon={FiUsers}
            title="Total Users"
            value={dashboardData.totalUsers.toLocaleString()}
            subtitle={`${dashboardData.activeUsers} active`}
            trend="+12% this month"
            color="bg-blue-600"
          />
          <StatCard
            icon={FiCalendar}
            title="Total Snipes"
            value={dashboardData.totalSnipes.toLocaleString()}
            subtitle={`${dashboardData.successfulSnipes} successful`}
            trend="+8% this week"
            color="bg-purple-600"
          />
          <StatCard
            icon={FiBarChart2}
            title="Success Rate"
            value={`${Math.round((dashboardData.successfulSnipes / dashboardData.totalSnipes) * 100)}%`}
            subtitle="Average success rate"
            trend="+2% improvement"
            color="bg-green-600"
          />
          <StatCard
            icon={FiDatabase}
            title="System Health"
            value={dashboardData.systemHealth}
            subtitle="All systems operational"
            color="bg-orange-600"
          />
        </div>

        {/* Heat Map Section */}
        <ClassTimingHeatMap />

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-8">
            <UserManagement 
              users={dashboardData.users} 
              onUserAction={handleUserAction}
            />
          </div>

          {/* Right Column */}
          <div className="space-y-8">
            <QuickActions onAction={handleQuickAction} />
            <RecentActivity activities={dashboardData.recentActivity} />
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  userName: state.profile.userName,
  email: state.profile.email,
  isAdmin: state.profile.isAdmin,
  membership: state.profile.membership
});

export default connect(mapStateToProps)(AdminDashboard); 